import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  BrandAsset,
  NavigationItem,
  NavigationMenu,
  NavigationSidebar,
  Text,
  TopNavRegionToggle,
} from "@certn/cascadia-react";
import { breakpoints } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { AdjudicationBetaBadge } from "../adjudication-beta/AdjudicationBetaBadge";
import { MonitoringNewBadge } from "../monitoring-new/MonitoringNewBadge";
import { NavigationSubtitle } from "./NavigationSubtitle";
import { useEntitledRegions } from "../../hooks/useEntitledRegions";
import { useUser } from "../../hooks/useUser";
import { FavouriteGroupsDropdownMenu } from "../../pages/groups/components/FavouriteGroupsDropDownMenu";
import { Schemas } from "../../types";
import {
  useCanCreatePackages,
  useCanAccessSettings,
  useCanViewAdjudication,
  useCanViewBillingProfiles,
  useCanViewProfiles,
  useDefaultSettingsRoute,
} from "../permissions/Permissions";
import { useCanViewMonitoring } from "../../pages/monitoring/useCanViewMonitoring";

export function AppNavigation() {
  // replacing code here to use while favourites is hidden behind a feature flag
  const { details: userDetails } = useUser();
  const { canSwitchRegion } = useEntitledRegions();
  const clientAccountName =
    userDetails?.client_account?.auth0_display_name || "";
  const { data: customerGroups = [] } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups/`
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: activeTasks } = useClientPortalAPI(
    "/api/internal/client-portal/me/tasks/",
    { status: "ACTIVE" }
  );
  const [favouritesOrderingParam, setFavouritesOrderingParam] =
    useState("created");
  const [sortedFavouriteGroups, setSortedFavouriteGroups] = useState<
    Schemas["FavouriteGroupsList"][]
  >([]);

  const {
    clientPortalPeopleProfile,
    clientPortalEnableGroupManagement,
    clientPortalEnableGroupFavourites,
    clientPortalPackageManagement,
    enableClientAdjudication,
    clientPortalEnableContinuousMonitoring,
  } = useFlags();

  const { data: favouriteGroups } = useClientPortalAPI(
    `/api/internal/client-portal/customer-groups/favourite-groups/`
  );
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );

  useEffect(() => {
    if (!favouriteGroups?.results) return;

    const sortedFavouriteGroups = [...favouriteGroups.results].sort((a, b) => {
      switch (favouritesOrderingParam) {
        case "created": // Sort by created (oldest first)
          return new Date(b.created).getTime() - new Date(a.created).getTime();

        case "-created": // Sort by created (newest first)
          return new Date(a.created).getTime() - new Date(b.created).getTime();

        case "customer_group__name": // Sort by name (A-Z)
          return a.customer_group.name.localeCompare(b.customer_group.name);

        case "-customer_group__name": // Sort by name (Z-A)
          return b.customer_group.name.localeCompare(a.customer_group.name);

        default:
          return 0; // No sorting if no valid parameter
      }
    });

    setSortedFavouriteGroups(sortedFavouriteGroups);
  }, [favouritesOrderingParam, favouriteGroups]);

  const hasPermissionProfiles = useCanViewProfiles();
  const hasPermissionsBilling = useCanViewBillingProfiles();
  const hasPermissionAdjudicationRubric = useCanViewAdjudication();
  const canAccessSettings = useCanAccessSettings();
  const defaultSettingsRoute = useDefaultSettingsRoute();
  const canViewMonitoring = useCanViewMonitoring();

  return (
    <NavigationSidebar
      width={isMobile ? "full" : "standard"}
      toolbar={
        <>
          <BrandAsset height="27" width="92" brand="certn" type="full" />
          {canSwitchRegion && <TopNavRegionToggle />}
        </>
      }
    >
      <NavigationMenu>
        <Text
          style="global/heading-large"
          className={styles.customerAccountName}
        >
          {clientAccountName}
        </Text>
        <NavigationItem
          key="home"
          icon="home"
          title={t("Home")}
          onPress={() => navigate("/")}
          isActive={location.pathname === "/"}
          data-testid="home-navigation-button"
        />
        <NavigationItem
          icon="task_alt"
          title={t("Tasks")}
          onPress={() => navigate("/tasks")}
          isActive={location.pathname.includes("/tasks")}
          badgeCount={activeTasks?.count ? activeTasks?.count : undefined}
          data-testid="tasks-navigation-button"
        />
        {clientPortalEnableGroupManagement && (
          <NavigationItem
            icon="workspaces"
            title={t("Groups")}
            onPress={() => navigate("/groups")}
            isActive={location.pathname.includes("/groups")}
            data-testid="groups-navigation-button"
          />
        )}
        <NavigationItem
          icon="patient_list"
          title={t("Cases")}
          onPress={() => navigate("/cases")}
          isActive={location.pathname.includes("/cases")}
          data-testid="cases-navigation-button"
        />
        {clientPortalEnableContinuousMonitoring && canViewMonitoring && (
          <NavigationItem
            icon="shield_radar"
            title={t("Monitoring")}
            onPress={() => navigate("/monitoring")}
            isActive={location.pathname.includes("/monitoring")}
            data-testid="monitoring-navigation-button"
            betaBadge={<MonitoringNewBadge />}
          />
        )}
        {clientPortalPeopleProfile && hasPermissionProfiles && (
          <NavigationItem
            icon="recent_actors"
            title={t("Profiles")}
            onPress={() => navigate("/profiles")}
            isActive={location.pathname.includes("/profiles")}
            data-testid="profiles-navigation-button"
          />
        )}
        {useCanCreatePackages() && clientPortalPackageManagement && (
          <NavigationItem
            title={t("Packages")}
            icon="package_2"
            onPress={() => navigate("/packages")}
            isActive={location.pathname.includes("/packages")}
            data-testid="packages-navigation-button"
          />
        )}
        <NavigationItem
          title={t("Apply links")}
          icon="link"
          onPress={() => navigate("/apply-links")}
          isActive={location.pathname.includes("/apply-links")}
          data-testid="apply-links-navigation-button"
        />
        {enableClientAdjudication && hasPermissionAdjudicationRubric && (
          <NavigationItem
            title={t("Adjudication")}
            icon="assignment_ind"
            onPress={() => navigate("/adjudication")}
            isActive={location.pathname.includes("/adjudication")}
            data-testid="adjudication-navigation-button"
            betaBadge={<AdjudicationBetaBadge />}
          />
        )}
        {hasPermissionsBilling && (
          <NavigationItem
            icon="receipt_long"
            title={t("Billing")}
            onPress={() => navigate("/billing/invoices")}
            isActive={location.pathname.includes("/billing/invoices")}
            data-testid="billing-navigation-button"
          />
        )}
        {canAccessSettings && (
          <NavigationItem
            icon="settings"
            title={t("Settings")}
            onPress={() => navigate(defaultSettingsRoute)}
            isActive={location.pathname.includes("/settings")}
            data-testid="settings-navigation-button"
          />
        )}
      </NavigationMenu>
      {clientPortalEnableGroupFavourites && (
        <NavigationMenu isScrollable>
          <div className={styles.favourites}>
            <NavigationSubtitle>{t("Favourites")}</NavigationSubtitle>
            {sortedFavouriteGroups.length > 0 && (
              <FavouriteGroupsDropdownMenu
                favouritesOrderingParam={favouritesOrderingParam}
                setFavouritesOrderingParam={setFavouritesOrderingParam}
              />
            )}
          </div>
          {sortedFavouriteGroups.length ? (
            sortedFavouriteGroups.map(({ customer_group }) => {
              return (
                <NavigationItem
                  key={customer_group.id}
                  title={customer_group.name}
                  onPress={() =>
                    navigate(
                      `/group-overview?customer_group_id=${customer_group.id}`,
                      {
                        state: { customer_group_id: customer_group.id },
                      }
                    )
                  }
                  isActive={
                    location.pathname.includes(`/group-overview`) &&
                    location.search.includes(
                      `customer_group_id=${customer_group.id}`
                    )
                  }
                  data-testid="group-navigation-button"
                />
              );
            })
          ) : (
            <NavigationSubtitle>
              <Text style="global/footer-small">
                {t("No favourite groups yet...")}
              </Text>
            </NavigationSubtitle>
          )}
        </NavigationMenu>
      )}
      {!clientPortalEnableGroupFavourites && (
        <NavigationMenu title={t("Groups")} isScrollable>
          <NavigationSubtitle>{t("Groups")}</NavigationSubtitle>
          {customerGroups.map(({ id, name }) => (
            <NavigationItem
              key={id}
              title={name || "Undefined"}
              onPress={() =>
                navigate(`/group-overview?customer_group_id=${id}`, {
                  state: { customer_group_id: id },
                })
              }
              isActive={
                location.pathname.includes(`/group-overview`) &&
                location.search.includes(`customer_group_id=${id}`)
              }
              data-testid="group-navigation-button"
            />
          ))}
          {!customerGroups.length && (
            <NavigationItem
              title={clientAccountName}
              wrapTitle
              onPress={() => navigate("/cases")}
              isActive={location.pathname.includes("/cases")}
              data-testid="client-account-navigation-button"
            />
          )}
        </NavigationMenu>
      )}
    </NavigationSidebar>
  );
}

const styles = {
  favourites: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  }),
  customerAccountName: css({
    display: "block",
    boxSizing: "border-box",
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
    marginBottom: "var(--space-1-5)",
    width: 0,
    minWidth: "100%",
    whiteSpace: "normal",
    overflowWrap: "break-word",
  }),
};
